import cn from 'classnames';
import { forwardRef, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { Button, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, Popup, PopupContent, PopupTrigger } from '../../..';
import { extractDataAriaAttributes, useFieldId } from '../../../utils';
import { CloseIcon, InfoSmallIcon } from '../../foundations';
import { BaseInput } from '../../inputs/base-input';
import { FieldLabel } from '../../internal';
import { Tooltip } from '../../popovers';
import { CalendarGrid } from '../calendar-grid';
import { DateSegments } from '../date-segments';
import { TimeSelector } from '../time-selector';

import { useDateTimePicker } from './use-date-time-picker';

import type { CalendarDate } from '@internationalized/date';
import type { ForwardedRef, KeyboardEvent, MouseEvent, ReactElement, SyntheticEvent } from 'react';

import type { DateSegmentsRef } from '../date-segments/date-segments.types';
import type { DateTimePickerProps } from './date-time-picker.types';

import './date-time-picker.scss';

/**
 * DateTimePicker is a composite component that combines date and time selection.
 * It provides a text field for entering date/time values and a popup with both
 * a calendar for date selection and time selectors for hour, minute, and second.
 *
 * Built on top of React Aria's date picker primitives with extended time support.
 * The component automatically handles keyboard navigation, accessibility, and
 * internationalization for both date and time components.
 *
 * ## Features
 *
 * - **Unified Date/Time Selection**: Single component for both date and time
 * - **Calendar Integration**: Full calendar for date selection
 * - **Time Selectors**: Dropdown selectors for hour, minute, and second
 * - **Granularity Control**: Configure precision from day to second level
 * - **Keyboard Navigation**: Full keyboard support with Enter/Space to open
 * - **Accessibility**: Screen reader support and proper focus management
 * - **Internationalization**: Automatic formatting based on locale
 * - **Validation**: Built-in min/max date/time validation
 * - **Form Integration**: Works seamlessly with HTML forms
 *
 * ## Usage
 *
 * ### Basic Usage
 * ```tsx
 * import { DateTimePicker } from '@bloomreach/react-banana-ui';
 *
 * function MyComponent() {
 *   const [value, setValue] = useState<Date | null>(new Date());
 *
 *   return (
 *     <DateTimePicker
 *       value={value}
 *       onChange={(newValue) => setValue(newValue)}
 *       granularity="minute"
 *     />
 *   );
 * }
 * ```
 *
 * ### With Validation
 * ```tsx
 * <DateTimePicker
 *   value={value}
 *   onChange={(newValue, context) => {
 *     if (context.valid) {
 *       setValue(newValue);
 *     } else {
 *       setError(context.validationMessage.join(' '));
 *     }
 *   }}
 *   minDate={new Date('2024-01-01')}
 *   maxDate={new Date('2024-12-31')}
 *   granularity="second"
 *   required
 * />
 * ```
 *
 * ## Accessibility
 *
 * The component follows WAI-ARIA guidelines for both date and time selection:
 * - Proper focus management between input, calendar, and time selectors
 * - Screen reader announcements for value changes
 * - Keyboard navigation with arrow keys in calendar
 * - Escape key to close, Enter/Space to open
 * - Support for assistive technologies
 *
 * @param props - The props for the DateTimePicker component
 * @param forwardedRef - Ref to be forwarded to the container element
 * @returns A comprehensive date and time picker component
 */
const DateTimePicker = forwardRef(
  (props: DateTimePickerProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      className,
      clearable = false,
      disabled,
      error,
      granularity = 'minute',
      helperText,
      hourCycle = 12,
      id,
      label,
      onOpenChange,
      readOnly,
      required,
      tooltip,
    } = props;
    const dataAriaAttributes = extractDataAriaAttributes(props);

    const controlId = useFieldId(id, 'rbui-date-time-picker-id');

    const ref = useRef<HTMLDivElement>(null);
    const dateSegmentsRef = useRef<DateSegmentsRef>(null);
    const mergedRef = mergeRefs([ref, forwardedRef]);
    const [lockFocus, setLockFocus] = useState(false);

    // Set up component logic and props
    const dateTimePickerProps = useDateTimePicker(props, ref);

    const {
      calendarProps: { calendarProps, calendarRef, calendarState, nextButtonProps, prevButtonProps, title },
      dateFieldProps: { dateFieldInputRef, dateFieldProps, dateFieldRef, dateFieldState, groupProps, hiddenInputProps },
      datePickerState,
      popupProps: { dialogProps },
      timeFieldProps: { onTimeChange, timeValue },
    } = dateTimePickerProps;

    const handleOnOpenChange = (o: boolean): void => {
      onOpenChange?.(o);
      datePickerState.setOpen(o);
      if (!o) {
        setLockFocus(false);
        calendarState.setFocusedDate(datePickerState.value as CalendarDate);
      }
    };

    const handleClick = (): void => {
      datePickerState.open();
      onOpenChange?.(true);
      calendarState.setFocused(false);
      setLockFocus(false);
    };

    const handleKeyDownOnEnter = (): void => {
      datePickerState.open();
      onOpenChange?.(true);
      calendarState.setFocused(true);
      setLockFocus(true);
    };

    const handleClear = (e: SyntheticEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      datePickerState.setValue(null);
      calendarState.setValue(null);
    };

    const getClearButton = (): ReactElement => {
      const hasValue = !!datePickerState.value;
      const hideClear = disabled || readOnly || !hasValue;

      return (
        <Button
          aria-label="Clear"
          className={cn('rbui-base-input__clear-button', {
            'rbui-base-input__clear-button--hidden': hideClear,
          })}
          onClick={handleClear}
          // Do not trigger onBlur when clear input
          onMouseDown={(e) => e.preventDefault()}
          startIcon={<CloseIcon />}
          type="tertiary"
        />
      );
    };

    const hasLabel = Boolean(label);
    const hasHelper = Boolean(helperText);

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required}>
        {label === ' ' ? <span>&nbsp;</span> : label}
      </FieldLabel>
    );

    const control = (
      <Popup onOpenChange={handleOnOpenChange} open={datePickerState.isOpen}>
        <PopupTrigger>
          <BaseInput
            className={cn('rbui-date-time-input', className)}
            disabled={disabled}
            error={error}
            id={controlId}
            inputProps={{
              // ...dateFieldProps,
              onClick: (e: MouseEvent<HTMLElement>) => {
                dateFieldProps.onClick?.(e);
                handleClick();
              },
              onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
                dateFieldProps.onKeyDown?.(e);
                if (e.key === 'Enter' || e.key === ' ') {
                  handleKeyDownOnEnter();
                }
              },
            }}
            leftElement={
              <CalendarIcon
                className="rbui-date-time-picker__calendar-icon"
                onClick={() => dateSegmentsRef.current?.focusFirstSegment()}
              />
            }
            readOnly={readOnly}
            ref={mergedRef}
            renderInput={(inputProps) => {
              return (
                <>
                  <span {...inputProps} ref={dateFieldRef}>
                    <DateSegments ref={dateSegmentsRef} state={dateFieldState} />
                  </span>
                  <input {...hiddenInputProps} ref={dateFieldInputRef} />
                </>
              );
            }}
            required={required}
            rightElement={clearable && getClearButton()}
            {...groupProps}
            onClick={(e: MouseEvent<HTMLElement>) => {
              groupProps.onClick?.(e);
              handleClick();
            }}
            {...dataAriaAttributes}
          />
        </PopupTrigger>
        <PopupContent initialFocus={lockFocus ? 0 : -1} modalFocus={true} {...dialogProps} asChild>
          <div className={cn('rbui-date-time-picker__popup-content')} ref={mergedRef}>
            <div {...calendarProps} className="rbui-date-time-picker__calendar" ref={calendarRef}>
              <header className="rbui-date-time-picker__calendar-header">
                <Button {...prevButtonProps} startIcon={<ChevronLeftIcon />} type="tertiary" />
                <span className="rbui-date-time-picker__calendar-header-title">{title}</span>
                <Button {...nextButtonProps} startIcon={<ChevronRightIcon />} type="tertiary" />
              </header>

              <div className="rbui-date-time-picker__calendar-body">
                <CalendarGrid state={calendarState} />
              </div>
            </div>

            {granularity !== 'day' && (
              <TimeSelector
                className="rbui-date-time-picker__time-selector"
                disabled={disabled}
                granularity={granularity}
                hourCycle={hourCycle}
                onChange={onTimeChange}
                readOnly={readOnly}
                value={timeValue}
              />
            )}
          </div>
        </PopupContent>
      </Popup>
    );

    return (
      <div
        className={cn('rbui-date-time-picker-field', className, {
          'rbui-date-time-picker-field--error': error,
          'rbui-date-time-picker-field--has-helper': hasHelper,
          'rbui-date-time-picker-field--has-label': hasLabel,
        })}
      >
        {hasLabel && (
          <div className="rbui-date-time-picker-field__label">
            {fieldLabel}
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoSmallIcon className="rbui-date-time-picker-field__icon" />
              </Tooltip>
            )}
          </div>
        )}
        <div className="rbui-date-time-picker-field__control">{control}</div>
        {hasHelper && <span className="rbui-date-time-picker-field__helper">{helperText}</span>}
      </div>
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';

export default DateTimePicker;
